import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useNavigate } from 'react-router-dom';
import { Item, RentalPeriod } from '../types';
import { formatGBP } from '../utils/currency';
import StarRating from './StarRating';
import './MyRentals.css';

// Base64 encoded simple SVG placeholder
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNODAgNjBIMTIwVjE0MEg4MFY2MFoiIGZpbGw9IiNEREQiLz48cGF0aCBkPSJNNjUgOTBIMTM1VjExMEg2NVY5MFoiIGZpbGw9IiNEREQiLz48L3N2Zz4=';

interface Rental {
  item: Item;
  startDate: string;
  endDate: string;
  totalCost: number;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled';
  hireRating?: {
    rating: number;
    review: string;
    date: string;
  };
}

interface UnreadMessages {
  [rentalId: string]: number;
}

const MyRentals: React.FC = () => {
  const { user } = useAuth();
  const { getUserRentals, isUsingSupabaseData } = useData();
  const navigate = useNavigate();
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [unreadMessages, setUnreadMessages] = useState<UnreadMessages>({});

  useEffect(() => {
    const loadUserRentals = async () => {
      if (user) {
        setLoading(true);
        try {
          const userRentals = await getUserRentals(user.id);
          
          // Always use Supabase data when connected, even if empty
          // For now, we'll show empty state since rental transformation needs more work
          setRentals([]);
        } catch (error) {
          console.error('Error loading user rentals:', error);
          setRentals([]);
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserRentals();
  }, [user, getUserRentals]);

  if (!user) {
    return <div>Please log in to view your rentals.</div>;
  }

  if (loading) {
    return (
      <div className="my-rentals">
        <div className="my-rentals-header">
          <h1>My Rentals</h1>
        </div>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Loading your rentals...
        </div>
      </div>
    );
  }

  const upcomingRentals = rentals.filter(rental => 
    rental.status === 'upcoming' || rental.status === 'active'
  );

  const pastRentals = rentals.filter(rental =>
    rental.status === 'completed' || rental.status === 'cancelled'
  );

  const handleRentalClick = (rental: Rental) => {
    navigate(`/rentals/${rental.item.id}-${rental.startDate}`);
  };

  return (
    <div className="my-rentals">
      <div className="my-rentals-header">
        <h1>My Rentals</h1>
        <div className="rental-tabs">
          <button
            className={`tab-button ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => setActiveTab('upcoming')}
          >
            Upcoming & Active
          </button>
          <button
            className={`tab-button ${activeTab === 'past' ? 'active' : ''}`}
            onClick={() => setActiveTab('past')}
          >
            Past Rentals
          </button>
        </div>
      </div>

      <div className="rentals-grid">
        {(activeTab === 'upcoming' ? upcomingRentals : pastRentals).map(rental => {
          const rentalId = `${rental.item.id}-${rental.startDate}`;
          return (
            <div
              key={rentalId}
              className="rental-card"
              onClick={() => handleRentalClick(rental)}
              style={{ cursor: 'pointer' }}
            >
              <div className="rental-image-container">
                {unreadMessages[rentalId] > 0 && (
                  <div className="message-notification">
                    <span className="notification-icon">💬</span>
                    <span className="notification-count">{unreadMessages[rentalId]}</span>
                  </div>
                )}
                <img
                  src={rental.item.images[0]}
                  alt={rental.item.name}
                  className="rental-image"
                />
                <div className={`rental-status ${rental.status}`}>
                  {rental.status.charAt(0).toUpperCase() + rental.status.slice(1)}
                </div>
              </div>
              <div className="rental-info">
                <h3>{rental.item.name}</h3>
                <p className="rental-dates">
                  {new Date(rental.startDate).toLocaleDateString()} - {new Date(rental.endDate).toLocaleDateString()}
                </p>
                <div className="rental-cost">
                  <span className="total-cost">{formatGBP(rental.totalCost)}</span>
                </div>

                {/* Show rating and review for past rentals */}
                {activeTab === 'past' && rental.hireRating && (
                  <div className="hire-rating">
                    <div className="rating-header">
                      <h4>Owner's Review</h4>
                      <StarRating rating={rental.hireRating.rating} totalRatings={1} size="small" />
                    </div>
                    <p className="rating-review">"{rental.hireRating.review}"</p>
                    <p className="rating-date">
                      Reviewed on {new Date(rental.hireRating.date).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {/* Only show cancel button for upcoming rentals */}
                {activeTab === 'upcoming' && rental.status === 'upcoming' && (
                  <button
                    className="cancel-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to cancel this rental?')) {
                        // TODO: Implement cancel rental API call
                        console.log('Cancelling rental:', rental.item.id);
                      }
                    }}
                  >
                    Cancel Rental
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {(activeTab === 'upcoming' ? upcomingRentals : pastRentals).length === 0 && (
          <div className="no-rentals">
            <p>No {activeTab === 'upcoming' ? 'upcoming or active' : 'past'} rentals found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRentals; 